import { validCompositionMetadataTree } from "../design/catalogue-runtime.js";
import { validCellDesignVariant } from "../design/visual-variants.js";
import type { LayoutDocument, LayoutNode } from "../layout/types.js";
import type { CustomDisciplineCellLayoutDefinition } from "./types.js";

/** An explicit content-sized root opts a modal section into natural height. */
export function customDisciplineLayoutFitsContent(
  document: LayoutDocument | null | undefined,
): boolean {
  const root = document?.scene?.children?.[0];
  return Boolean(
    document?.scene?.children?.length === 1 &&
      root?.kind === "container" &&
      root.layout === "flex" &&
      root.flexHeightMode === "fitContent" &&
      root.style?.position === "relative",
  );
}

/** Root container id for persisted custom-discipline cell layouts. */
export const CUSTOM_DISCIPLINE_CELL_ROOT_ID = "custom-discipline-cell-root";

/** Default height for cell layouts created before configurable heights. */
export const CUSTOM_DISCIPLINE_CELL_HEIGHT = 52;

const CELL_WIDTH_MATCH_EPSILON = 1.5;

function nodeWidthMatchesCellCanvas(
  nodeWidth: number,
  canvasWidth: number,
): boolean {
  if (
    !Number.isFinite(nodeWidth) ||
    !Number.isFinite(canvasWidth) ||
    canvasWidth <= 0
  ) {
    return false;
  }
  return (
    Math.abs(nodeWidth - canvasWidth) <= CELL_WIDTH_MATCH_EPSILON ||
    nodeWidth >= canvasWidth * 0.95
  );
}

/** Resizes spanning elements with the canvas, preserving typography and insets. */
export function resizeCustomDisciplineColumnLayoutDocument(
  document: LayoutDocument,
  targetWidth: number,
  targetHeight = document.meta.canvas.height,
): LayoutDocument {
  const previousWidth = document.meta.canvas.width;
  const previousHeight = document.meta.canvas.height;
  const width =
    Number.isFinite(targetWidth) && targetWidth > 0
      ? targetWidth
      : previousWidth;
  const height =
    Number.isFinite(targetHeight) && targetHeight > 0
      ? targetHeight
      : previousHeight;
  if (width === previousWidth && height === previousHeight) return document;

  const resizeDimension = (
    size: number,
    offset: number,
    previous: number,
    next: number,
    allowInsets: boolean,
  ) => {
    if (previous === next) return size;
    const endInset = previous - offset - size;
    if (
      allowInsets &&
      offset >= 0 &&
      offset <= 24 &&
      endInset >= 0 &&
      endInset <= 24 &&
      size >= previous * 0.8
    )
      return Math.max(1, next - offset - endInset);
    return Math.abs(size - previous) <= CELL_WIDTH_MATCH_EPSILON ? next : size;
  };
  const resize = (
    node: LayoutNode,
    previousParentWidth: number,
    parentWidth: number,
    previousParentHeight: number,
    parentHeight: number,
  ): LayoutNode => {
    const allowInsets =
      node.kind === "container" ||
      (node.kind === "primitive" &&
        (node.primitive === "text" || node.primitive === "shape"));
    const width = resizeDimension(
      node.width,
      node.x,
      previousParentWidth,
      parentWidth,
      allowInsets,
    );
    const height = resizeDimension(
      node.height,
      node.y,
      previousParentHeight,
      parentHeight,
      allowInsets,
    );
    return {
      ...node,
      width,
      height,
      ...(node.kind === "container"
        ? {
            children: node.children.map((child) =>
              resize(child, node.width, width, node.height, height),
            ),
          }
        : {}),
    };
  };
  return {
    ...document,
    meta: {
      ...document.meta,
      canvas: { ...document.meta.canvas, width, height },
    },
    scene: {
      ...document.scene,
      children: document.scene.children.map((node) =>
        resize(node, previousWidth, width, previousHeight, height),
      ),
    },
  };
}

function resizeCellLayoutNodeTree(
  node: LayoutNode,
  previousCanvasWidth: number,
  targetWidth: number,
): LayoutNode {
  const isRoot = node.id === CUSTOM_DISCIPLINE_CELL_ROOT_ID;
  const nextWidth = isRoot
    ? targetWidth
    : nodeWidthMatchesCellCanvas(node.width, previousCanvasWidth)
      ? targetWidth
      : node.width;

  const nextNode: LayoutNode = {
    ...node,
    width: nextWidth,
    ...(isRoot
      ? {
          x: 0,
          y: 0,
          locked: true,
        }
      : {}),
  };

  if (nextNode.kind !== "container" || !Array.isArray(nextNode.children)) {
    return nextNode;
  }

  return {
    ...nextNode,
    children: nextNode.children.map((child) =>
      resizeCellLayoutNodeTree(child, previousCanvasWidth, targetWidth),
    ),
  };
}

/**
 * Resizes a cell layout document to match the current DataGrid column width.
 * Updates the canvas, root container, and descendants that span the previous canvas width.
 */
export function resizeCustomDisciplineCellLayoutDocument(
  document: LayoutDocument,
  targetWidth: number,
): LayoutDocument {
  const normalizedWidth =
    Number.isFinite(targetWidth) && targetWidth > 0
      ? targetWidth
      : document.meta.canvas.width;
  const previousCanvasWidth = document.meta.canvas.width || normalizedWidth;

  return {
    ...document,
    meta: {
      ...document.meta,
      canvas: {
        ...document.meta.canvas,
        width: normalizedWidth,
      },
    },
    scene: {
      ...document.scene,
      children: document.scene.children.map((node) =>
        node.id === CUSTOM_DISCIPLINE_CELL_ROOT_ID
          ? resizeCellLayoutNodeTree(node, previousCanvasWidth, normalizedWidth)
          : node,
      ),
    },
  };
}

export type CustomDisciplineLayoutDocumentError =
  | "CUSTOM_DISCIPLINE_INVALID_LAYOUT_DOCUMENT"
  | "CUSTOM_DISCIPLINE_INVALID_CELL_LAYOUT";

export type CustomDisciplineModalLayoutSection =
  | "titleLayout"
  | "contentLayout"
  | "actionsLayout";

/** Default canvas sizes when configuring modal section layouts in admin. */
export const DEFAULT_MODAL_SECTION_CANVAS: Record<
  CustomDisciplineModalLayoutSection,
  { width: number; height: number }
> = {
  titleLayout: { width: 720, height: 72 },
  contentLayout: { width: 720, height: 360 },
  actionsLayout: { width: 720, height: 72 },
};

/**
 * Returns true when the value matches the minimal LayoutDocument shape persisted
 * by the Display Editor (distinct from behavior graphs).
 */
export function isValidCustomDisciplineLayoutDocument(
  value: unknown,
): value is Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const doc = value as Record<string, unknown>;
  if (typeof doc.version !== "number") {
    return false;
  }
  const meta = doc.meta;
  if (!meta || typeof meta !== "object" || Array.isArray(meta)) {
    return false;
  }
  const canvas = (meta as Record<string, unknown>).canvas;
  if (!canvas || typeof canvas !== "object" || Array.isArray(canvas)) {
    return false;
  }
  const canvasRecord = canvas as Record<string, unknown>;
  if (
    typeof canvasRecord.width !== "number" ||
    typeof canvasRecord.height !== "number" ||
    canvasRecord.width <= 0 ||
    canvasRecord.height <= 0
  ) {
    return false;
  }
  const scene = doc.scene;
  if (!scene || typeof scene !== "object" || Array.isArray(scene)) {
    return false;
  }
  return (
    Array.isArray((scene as Record<string, unknown>).children) &&
    validCompositionMetadataTree(value)
  );
}

/** Reads canvas dimensions from a persisted layout document. */
export function getCustomDisciplineLayoutCanvasSize(
  layout: Record<string, unknown> | null | undefined,
): { width: number; height: number } | null {
  if (!isValidCustomDisciplineLayoutDocument(layout)) {
    return null;
  }
  const meta = layout.meta as { canvas: { width: number; height: number } };
  return {
    width: meta.canvas.width,
    height: meta.canvas.height,
  };
}

/** Parses JSON strings and validates layout documents from API / form state. */
export function coerceCustomDisciplineLayoutDocument(
  value: unknown,
): Record<string, unknown> | null {
  if (value == null) {
    return null;
  }
  let candidate: unknown = value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    try {
      candidate = JSON.parse(trimmed) as unknown;
    } catch {
      return null;
    }
  }
  return isValidCustomDisciplineLayoutDocument(candidate)
    ? (candidate as Record<string, unknown>)
    : null;
}

/** Resolves initial canvas size for a modal section editor. */
export function resolveModalSectionCanvasSize(
  section: CustomDisciplineModalLayoutSection,
  layout: Record<string, unknown> | null | undefined,
  modalWidth?: number,
  modalHeight?: number,
  contentMaxHeight?: number | null,
): { width: number; height: number } {
  const fromLayout = getCustomDisciplineLayoutCanvasSize(layout);
  if (fromLayout) {
    return fromLayout;
  }
  const defaults = DEFAULT_MODAL_SECTION_CANVAS[section];
  const width =
    Number.isFinite(modalWidth) && (modalWidth ?? 0) > 0
      ? (modalWidth as number)
      : defaults.width;
  if (section === "contentLayout") {
    const contentHeight =
      contentMaxHeight && contentMaxHeight > 0
        ? contentMaxHeight
        : Math.max(120, (modalHeight ?? 520) - 160);
    return { width, height: contentHeight };
  }
  return { width, height: defaults.height };
}

/** Validates a cell-layout wrapper ({ enabled, defaultWidth, layout, … }). */
export function isValidCustomDisciplineCellLayoutDefinition(
  value: unknown,
): value is CustomDisciplineCellLayoutDefinition {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  if (!validCompositionMetadataTree(value)) return false;
  const cell = value as Record<string, unknown>;
  if (typeof cell.enabled !== "boolean") {
    return false;
  }
  if (typeof cell.defaultWidth !== "number" || cell.defaultWidth <= 0) {
    return false;
  }
  if (typeof cell.resizable !== "boolean") {
    return false;
  }
  if (cell.designVariants != null) {
    const variants = cell.designVariants as Record<string, unknown>;
    if (
      typeof variants !== "object" ||
      Array.isArray(variants) ||
      (variants.modern != null && !validCellDesignVariant(variants.modern))
    )
      return false;
  }
  for (const layout of [cell.layout, cell.headerLayout]) {
    if (layout != null && !isValidCustomDisciplineLayoutDocument(layout))
      return false;
  }
  if (cell.headerGroup != null) {
    if (typeof cell.headerGroup !== "object" || Array.isArray(cell.headerGroup))
      return false;
    const group = cell.headerGroup as Record<string, unknown>;
    if (
      typeof group.key !== "string" ||
      !group.key.trim() ||
      typeof group.label !== "string"
    )
      return false;
    if (
      group.layout != null &&
      !isValidCustomDisciplineLayoutDocument(group.layout)
    )
      return false;
  }
  return true;
}

/** Validates a layout document; returns an error code or null. */
export function validateCustomDisciplineLayoutDocument(
  value: unknown,
): CustomDisciplineLayoutDocumentError | null {
  if (value === null || value === undefined) {
    return null;
  }
  return isValidCustomDisciplineLayoutDocument(value)
    ? null
    : "CUSTOM_DISCIPLINE_INVALID_LAYOUT_DOCUMENT";
}

/** Validates a cell layout wrapper; returns an error code or null. */
export function validateCustomDisciplineCellLayoutDefinition(
  value: unknown,
): CustomDisciplineLayoutDocumentError | null {
  if (value === null || value === undefined) {
    return null;
  }
  return isValidCustomDisciplineCellLayoutDefinition(value)
    ? null
    : "CUSTOM_DISCIPLINE_INVALID_CELL_LAYOUT";
}
